// ---------------------------------------------------------------------------
// QuestionPickerDialog — Modal to select a question from the bank
// ---------------------------------------------------------------------------

import { useState, useMemo } from 'react';
import { Search, Plus } from 'lucide-react';
import { useQuestions, useCreateQuestion, useDiseaseAssociations } from '@/hooks/use-questions';
import { useAuth } from '@/lib/auth';
import { canCreateQuestion } from '@/types/workflow';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import type { Question, QuestionFilters, CreateQuestionRequest } from '@/types/question';
import type { AlertLevel } from '@/types/template';

interface QuestionPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (question: Question) => void;
}

export function QuestionPickerDialog({ open, onOpenChange, onSelect }: QuestionPickerDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [keyword, setKeyword] = useState('');
  const [disease, setDisease] = useState('__all__');
  const [showCreate, setShowCreate] = useState(false);
  const { data: diseases = [] } = useDiseaseAssociations();

  const filters: QuestionFilters = useMemo(() => ({
    keyword: keyword || undefined,
    diseaseAssociation: disease === '__all__' ? undefined : disease,
    page: 1,
    pageSize: 20,
  }), [keyword, disease]);

  const { data, isLoading } = useQuestions(filters);
  const createMutation = useCreateQuestion();

  const [newText, setNewText] = useState('');
  const [newAnswers, setNewAnswers] = useState<{ text: string; level: AlertLevel }[]>([
    { text: '', level: 0 },
  ]);

  async function handleCreateAndSelect() {
    try {
      const result = await createMutation.mutateAsync({
        questionText: newText,
        answers: newAnswers
          .filter((a) => a.text.trim())
          .map((a, i) => ({
            answerText: a.text,
            alertLevel: a.level,
            displayOrder: i + 1,
          })),
      } as CreateQuestionRequest);
      toast({ title: 'Question created', variant: 'success' });
      onSelect(result);
      onOpenChange(false);
    } catch {
      // Error handled by mutation
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pick a Question</DialogTitle>
          <DialogDescription>Select an existing question or create a new one.</DialogDescription>
        </DialogHeader>

        {showCreate ? (
          <div className="space-y-3 mt-2">
            <div>
              <label className="block text-sm font-medium text-carbon-700 mb-1">
                Question Text
              </label>
              <textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                className="flex w-full rounded-ds-004 border border-silver-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 min-h-[60px]"
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-carbon-700">Answers</label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setNewAnswers((p) => [...p, { text: '', level: 0 }])}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-1.5">
                {newAnswers.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      placeholder={`Answer ${i + 1}`}
                      value={a.text}
                      onChange={(e) => {
                        const next = [...newAnswers];
                        next[i] = { ...next[i], text: e.target.value };
                        setNewAnswers(next);
                      }}
                      className="flex-1"
                    />
                    <select
                      value={a.level}
                      onChange={(e) => {
                        const next = [...newAnswers];
                        next[i] = { ...next[i], level: Number(e.target.value) as AlertLevel };
                        setNewAnswers(next);
                      }}
                      className="h-10 w-16 rounded-ds-004 border border-silver-300 bg-white px-2 text-sm"
                    >
                      {[0, 1, 2, 3, 4].map((l) => (
                        <option key={l} value={l}>L{l}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>
                Back
              </Button>
              <Button
                onClick={handleCreateAndSelect}
                disabled={!newText.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create & Select'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-carbon-400" />
                <Input
                  placeholder="Search questions..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={disease} onValueChange={setDisease}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="All Diseases" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Diseases</SelectItem>
                  {diseases.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {user && canCreateQuestion(user.role) && (
                <Button variant="secondary" size="sm" onClick={() => setShowCreate(true)}>
                  <Plus className="h-4 w-4 mr-1" /> New
                </Button>
              )}
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-16" />
                ))}
              </div>
            ) : data?.entries.length === 0 ? (
              <p className="text-sm text-carbon-500 text-center py-6">No questions found</p>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {data?.entries.map((q) => (
                  <Card
                    key={q.question_id}
                    className="cursor-pointer hover:border-purple-300 hover:bg-purple-50/30 transition-colors"
                    onClick={() => {
                      onSelect(q);
                      onOpenChange(false);
                    }}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-carbon-800">{q.question_text}</p>
                          <p className="text-xs text-carbon-400 mt-1">
                            {q.answers?.length ?? 0} answer{(q.answers?.length ?? 0) !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {q.disease_association && (
                            <Badge variant="outline" className="text-[10px]">
                              {q.disease_association}
                            </Badge>
                          )}
                          <Badge variant="secondary" className="text-[10px]">
                            Q-{q.question_id}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
